import React, { useRef } from 'react'
import { AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay, Button } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'

interface IDeleteConfirmationDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  type: 'user' | 'event' | 'profile' | 'category' | 'activity' | 'approve_activity' | 'blocker' | 'feedback_blocker'
}

function DeleteConfirmationDialog({ isOpen, onClose, onConfirm, type }: IDeleteConfirmationDialogProps): JSX.Element {
  const cancelRef = useRef<HTMLButtonElement>(null)
  const { t } = useTranslation()

  const getDeleteWarningTitle = (
    type: 'user' | 'event' | 'profile' | 'category' | 'activity' | 'approve_activity' | 'blocker' | 'feedback_blocker'
  ): string => {
    switch (type) {
      case 'user':
        return t('delete_title_user_warning')
      case 'event':
        return t('delete_title_event_warning')
      case 'profile':
        return t('delete_title_profile_warning')
      case 'category':
        return t('delete_title_category_warning')
      case 'activity':
        return t('delete_title_activity_warning')
      case 'approve_activity':
        return t('confirm_title_suggested_activity_warning')
      case 'blocker':
        return t('blocker_title_suggested_activity_warning')
      case 'feedback_blocker':
        return t('blocker_title_feedback_moderation_warning')
      default:
        return t('delete_title_generic_warning')
    }
  }

  const getDeleteWarningDescription = (
    type: 'user' | 'event' | 'profile' | 'category' | 'activity' | 'approve_activity' | 'blocker' | 'feedback_blocker'
  ): string => {
    switch (type) {
      case 'user':
        return t('delete_description_user_warning')
      case 'event':
        return t('delete_description_event_warning')
      case 'profile':
        return t('delete_description_profile_warning')
      case 'category':
        return t('delete_description_category_warning')
      case 'activity':
        return t('delete_description_activity_warning')
      case 'approve_activity':
        return t('confirm_description_suggested_activity_warning')
      case 'blocker':
        return t('blocker_description_suggested_activity_warning')
      case 'feedback_blocker':
        return t('blocker_description_feedback_moderation_warning')
      default:
        return t('delete_description_generic_warning')
    }
  }

  const getDeleteWarningButton = (
    type: 'user' | 'event' | 'profile' | 'category' | 'activity' | 'approve_activity' | 'blocker' | 'feedback_blocker'
  ): string => {
    switch (type) {
      case 'user':
        return t('delete_user')
      case 'event':
        return t('delete_event')
      case 'profile':
        return t('delete_profile')
      case 'category':
        return t('delete_category')
      case 'activity':
        return t('delete_activity')
      case 'approve_activity':
        return t('confirm_suggested_activity')
      case 'blocker':
        return t('blocker_suggested_activity')
      case 'feedback_blocker':
        return t('blocker_feedback_moderation')
      default:
        return t('delete_generic')
    }
  }

  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
      <AlertDialogOverlay>
        <AlertDialogContent padding={'10px'}>
          <AlertDialogHeader fontSize='24px' fontWeight='700' lineHeight='33.6px' textAlign='center'>
            {getDeleteWarningTitle(type)}
          </AlertDialogHeader>

          <AlertDialogBody fontSize='16px' fontWeight='400' lineHeight='22.4px' textAlign='center'>
            {getDeleteWarningDescription(type)}
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button variant={'secondary'} ref={cancelRef} onClick={onClose}>
              {t('cancel')}
            </Button>
            <Button variant={'primary'} onClick={onConfirm} ml={3}>
              {getDeleteWarningButton(type)}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}

export { DeleteConfirmationDialog }
